import type { App } from "@upsquad/server";
import type { LegalAiWizardInput, LegalAiWizardResponse, UpdateCommunityMobileAppPayload } from "@upsquad/shared";
import type { EdenCall } from "@/src/common/lib/eden-base-query";
import { baseApi } from "@/src/common/services/base-api";

export type ListStaffCommunitiesResponse = App["~Routes"]["staff"]["communities"]["get"]["response"]["200"];
export type GetCommunityMobileAppResponse = App["~Routes"]["community-mobile-app"][":communityId"]["get"]["response"]["200"];
export type UpdateCommunityMobileAppResponse = App["~Routes"]["community-mobile-app"][":communityId"]["patch"]["response"]["200"];
export type ImpersonateResponse = App["~Routes"]["staff"]["impersonate"][":userId"]["post"]["response"]["200"];
export type ExitImpersonationResponse = App["~Routes"]["staff"]["impersonate"]["exit"]["post"]["response"]["200"];

export type GenerateLegalDocumentPayload = LegalAiWizardInput & { communityId: string };

export const staffApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		listStaffCommunities: builder.query<ListStaffCommunitiesResponse, void>({
			query: (): EdenCall<ListStaffCommunitiesResponse> => (client) => client.staff.communities.get(),
			providesTags: ["StaffCommunities"],
		}),
		getCommunityMobileApp: builder.query<GetCommunityMobileAppResponse, string>({
			query:
				(communityId): EdenCall<GetCommunityMobileAppResponse> =>
				(client) =>
					client["community-mobile-app"]({ communityId }).get(),
			providesTags: (_result, _error, communityId) => [{ type: "StaffCommunityMobileApp", id: communityId }],
		}),
		updateCommunityMobileApp: builder.mutation<
			UpdateCommunityMobileAppResponse,
			{ communityId: string; payload: UpdateCommunityMobileAppPayload }
		>({
			query:
				({ communityId, payload }): EdenCall<UpdateCommunityMobileAppResponse> =>
				(client) =>
					client["community-mobile-app"]({ communityId }).patch(payload),
			invalidatesTags: (_result, _error, { communityId }) => [{ type: "StaffCommunityMobileApp", id: communityId }, "StaffCommunities"],
		}),
		generateLegalDocument: builder.mutation<LegalAiWizardResponse, GenerateLegalDocumentPayload>({
			query:
				({ communityId, ...body }): EdenCall<LegalAiWizardResponse> =>
				(client) =>
					client["community-mobile-app"]({ communityId })["legal-ai"].generate.post(body),
		}),
		impersonate: builder.mutation<ImpersonateResponse, string>({
			query:
				(userId): EdenCall<ImpersonateResponse> =>
				(client) =>
					client.staff.impersonate({ userId }).post({}),
		}),
		exitImpersonation: builder.mutation<ExitImpersonationResponse, void>({
			query: (): EdenCall<ExitImpersonationResponse> => (client) => client.staff.impersonate.exit.post({}),
		}),
	}),
});

export const {
	useListStaffCommunitiesQuery,
	useGetCommunityMobileAppQuery,
	useUpdateCommunityMobileAppMutation,
	useGenerateLegalDocumentMutation,
	useImpersonateMutation,
	useExitImpersonationMutation,
} = staffApi;
